import { createApi } from '@reduxjs/toolkit/query/react';
import { getBaseApiPath } from '@/app/common/api/config';
import customFetchBase from '@/app/common/utils/customFetchBase';
import {
  ClusterFromTaskRequest,
  ClusterFromTaskResponse,
  CreateMassiveTaskRequest,
  CreateMassiveTaskResponse,
  MassiveDeleteHistoryResponse,
  MassiveDiagramResponse,
  MassiveExcludedKeywordsResponse,
  MassiveHistoryResponse,
  MassiveStatusResponse,
  MassiveStopTaskResponse
} from '@/app/KeywordResearch/types/massiveApiTypes';
const massiveApi = createApi({
  reducerPath: 'massiveApi',
  tagTypes: ['historyMassive', 'massiveStatus'],
  baseQuery: (...args: unknown[]) =>
    customFetchBase(getBaseApiPath(), ...args),
  endpoints: build => ({
    createMassiveTask: build.mutation<
      CreateMassiveTaskResponse,
      CreateMassiveTaskRequest
    >({
      query: (body: CreateMassiveTaskRequest) => ({
        url: 'keyword_related/v1/massive/get_keywords',
        method: 'POST',
        body
      })
    }),
    getMassiveHistory: build.query<MassiveHistoryResponse, void>({
      query: () => ({
        url: 'keyword_related/v1/massive/history_search',
        method: 'POST'
      }),
      providesTags: ['historyMassive']
    }),
    getMassiveDetails: build.query({
      query: body => ({
        url: 'keyword_massive/v2/view_history_search',
        method: 'POST',
        body: body
      })
    }),
    getDiagram: build.query<MassiveDiagramResponse, string>({
      query: (task_id: string) => ({
        url: 'keyword_related/v1/massive/view_history_search_echart',
        method: 'POST',
        body: { task_id }
      })
    }),
    getMassiveStatus: build.query<MassiveStatusResponse, string>({
      query: (task_id: string) => ({
        url: 'keyword_related/v1/massive/get_status',
        method: 'POST',
        body: { task_id }
      }),
      providesTags: ['massiveStatus']
    }),
    getExcludedKeywords: build.query<MassiveExcludedKeywordsResponse, string>({
      query: (task_id: string) => ({
        url: 'keyword_related/v1/massive/get_excluded_keywords',
        method: 'POST',
        body: { task_id }
      })
    }),
    deleteMassiveHistory: build.mutation<MassiveDeleteHistoryResponse, string>({
      query: (task_id: string) => ({
        url: 'keyword_related/v1/massive/delete_history',
        method: 'POST',
        body: { task_id }
      }),
      invalidatesTags: ['historyMassive']
    }),
    stopMassiveProgress: build.mutation<MassiveStopTaskResponse, string>({
      query: (task_id: string) => ({
        url: 'keyword_related/v1/massive/stop_task',
        method: 'POST',
        body: { task_id }
      }),
      invalidatesTags: ['historyMassive', 'massiveStatus']
    }),
    clusterFromTask: build.mutation<
      ClusterFromTaskResponse,
      ClusterFromTaskRequest
    >({
      query: (body: ClusterFromTaskRequest) => ({
        url: 'keyword_related/v1/massive/cluster_from_task',
        method: 'POST',
        body
      })
    })
  })
});

export const {
  useGetMassiveHistoryQuery,
  useGetMassiveDetailsQuery,
  useLazyGetDiagramQuery,
  useCreateMassiveTaskMutation,
  useGetMassiveStatusQuery,
  useLazyGetExcludedKeywordsQuery,
  useDeleteMassiveHistoryMutation,
  useStopMassiveProgressMutation,
  useClusterFromTaskMutation
} = massiveApi;

export default massiveApi;
